'use client';

import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import {
  Sparkles,
  ShieldCheck,
  Brain,
  HeartPulse,
  Activity,
  Stethoscope,
  Users,
  FlaskConical,
  Bell,
  Salad,
  LineChart,
  Crosshair,
  Compass,
  HeartHandshake,
} from 'lucide-react';
import { resolveGuestVersion } from '@/lib/guest-version-genome';

const TOOL_ICONS = {
  recommendations: Sparkles,
  nutrition: Salad,
  metabolism: FlaskConical,
  neuroReset: Brain,
  progress: LineChart,
  triage: Crosshair,
  specialists: Stethoscope,
  community: Users,
  labs: FlaskConical,
  alerts: Bell,
  emotionalRegulation: HeartPulse,
};

export default function YourVersionPanel({
  goal,
  goals,
  feeling,
  feelings,
  age,
  gender,
  medicalFlags,
}) {
  const t = useTranslations('QuickHook.yourVersion');
  const version = useMemo(
    () => resolveGuestVersion({ goal, goals, feeling, feelings, age, gender, medicalFlags }),
    [goal, goals, feeling, feelings, age, gender, medicalFlags]
  );

  return (
    <div className="space-y-4 rounded-2xl border border-cyan-400/35 bg-gradient-to-br from-cyan-950/40 via-slate-950/90 to-emerald-950/30 p-4 md:p-5 shadow-[0_0_36px_rgba(6,182,212,0.18)]">
      {/* A + B: qué recibiste + qué es aifit */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="text-left">
          <p className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.22em] text-cyan-300">
            <Compass size={12} aria-hidden />
            {t('badge')}
          </p>
          <h4 className="mt-1 text-xl font-black text-white md:text-2xl">{t('title')}</h4>
          <p className="mt-2 text-sm leading-relaxed text-slate-200">{t('whatIsBody')}</p>
        </div>
        <div className="shrink-0 rounded-2xl border border-cyan-400/30 bg-black/40 px-3 py-2 text-center">
          <p className="text-[9px] font-black uppercase tracking-[0.18em] text-slate-400">
            {t('genomeLabel')}
          </p>
          <p className="mt-1 font-mono text-lg font-black tracking-wider text-cyan-200">
            {version.genomeId}
          </p>
        </div>
      </div>

      {/* Enfoque de hoy (perfil) */}
      <div className="rounded-xl border border-white/10 bg-slate-900/60 p-3 text-left">
        <p className="text-[11px] font-black uppercase tracking-[0.16em] text-emerald-300">
          {t(`profiles.${version.profileKey}.label`)}
        </p>
        <p className="mt-1 text-sm font-semibold text-white">
          {t(`profiles.${version.profileKey}.headline`)}
        </p>
        <p className="mt-1 text-xs leading-relaxed text-slate-300">
          {t(`profiles.${version.profileKey}.promise`)}
        </p>
      </div>

      {/* C: honestidad */}
      <div className="rounded-xl border border-white/10 bg-slate-900/50 p-3 text-left">
        <p className="inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-[0.16em] text-slate-300">
          <HeartHandshake size={13} aria-hidden />
          {t('honestyTitle')}
        </p>
        <p className="mt-1.5 text-xs leading-relaxed text-slate-200">{t('honestyBody')}</p>
      </div>

      {/* D: diferencia vs internet / chat genérico (sin marcas) */}
      <div className="rounded-xl border border-amber-400/20 bg-amber-500/5 p-3 text-left">
        <p className="text-[11px] font-black uppercase tracking-[0.16em] text-amber-300">
          {t('diffTitle')}
        </p>
        <ul className="mt-2 space-y-2">
          {['diff1', 'diff2', 'diff3'].map((key) => (
            <li key={key} className="flex gap-2 text-xs leading-relaxed text-slate-200">
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-300/80" aria-hidden />
              <span>{t(key)}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Herramientas priorizadas hoy */}
      <div>
        <p className="mb-2 inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-[0.16em] text-cyan-300">
          <ShieldCheck size={13} aria-hidden />
          {t('toolsTitle')}
        </p>
        <p className="mb-3 text-xs text-slate-400">{t('toolsSubtitle')}</p>
        <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {version.toolIds.map((toolId) => {
            const Icon = TOOL_ICONS[toolId] || Activity;
            return (
              <li
                key={toolId}
                className="rounded-xl border border-cyan-500/20 bg-cyan-500/5 px-3 py-2.5 text-left"
              >
                <p className="inline-flex items-center gap-1.5 text-xs font-black text-cyan-100">
                  <Icon size={14} aria-hidden />
                  {t(`tools.${toolId}.name`)}
                </p>
                <p className="mt-1 text-[11px] leading-relaxed text-slate-300">
                  {t(`tools.${toolId}.why`)}
                </p>
              </li>
            );
          })}
        </ul>
      </div>

      {/* E: por qué guardar */}
      <p className="text-[11px] leading-relaxed text-slate-400">{t('evolutionNote')}</p>
    </div>
  );
}
